import { ReactElement, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Header from 'containers/Header';
import Table from 'containers/Table';
import { createStructuredSelector } from 'reselect';
import { makeSelectTokenAccessToken } from 'store/oauth/selectors';
import { Redirect, useHistory, useParams } from 'react-router-dom';
import { LiteralHeadersTableTitles, RouteTypes } from 'consts';
import './practice-list.scss';
import { RfUiBreadcrumbs, RfUiModal } from 'renfe-components';
import { makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected } from 'store/usuarios/selectors';
import { setLoading } from 'store/app/actions';
import {
  getPracticas,
  getPracticasCount,
  getPracticasExcel,
  setPracticasCount2,
} from 'store/practicas/actions';
import {
  makeSelectFechaMinimaPractica,
  makeSelectPracticasList,
  makeSelectPracticasListCount,
} from 'store/practicas/selectors';
import {
  parseDate,
  parsePracticeStatus,
  parseTime,
} from 'utils/parsePractices';
import { hasAccess } from 'utils/listUtils';
import { userProfile } from 'utils/profilesIndex';
import { getDefaultStatus } from 'utils/practiceUtils';
import { useList } from 'hooks/list';
import { CourseAlumn } from 'types';
import { getTodayFinishDate, getTodayMinus15 } from 'utils/dates-validators';
import { useLocation } from 'react-router';
import {
  makeSelectModalDraftType,
  makeSelectMsgSubmit,
} from 'store/app/selectors';
import {
  getInfoUserProfile,
  setModalDraftType,
  setPracticaReclamadaSeleccionada,
  setPracticaValidadaSeleccionada,
  setMsgSubmit,
} from 'store/app/actions';
import AlumnPracticeListFilter from './alumnfilter';

const selectors: any = createStructuredSelector({
  access_token: makeSelectTokenAccessToken(),
  msgSubmit: makeSelectMsgSubmit(),
  modalDraftType: makeSelectModalDraftType(),
  profileActive: makeSelectPerfilesUsuarioDatosPerfilesUsuarioSelected(),
  practicas: makeSelectPracticasList(),
  practicasCount: makeSelectPracticasListCount(),
  fechaMinimaPractica: makeSelectFechaMinimaPractica(),
} as any);

const options =
  '[{"label": "Fecha inicio de menor a mayor","value": {"order":"FECHA_INI_SERVICIO","ascDesc": "asc"},"active": "true"},{"label": "Fecha inicio de mayor a menor","value": {"order":"FECHA_INI_SERVICIO","ascDesc": "desc"},"active": "false"},{"label": "Fecha fin de mayor a menor","value": {"order":"FECHA_FIN_SERVICIO","ascDesc": "desc"},"active": "false"},{"label": "Fecha fin de menor a mayor","value": {"order":"FECHA_FIN_SERVICIO","ascDesc": "asc"},"active": "false"}]';

const listAlumnosCursosConstant = 'Listado de alumnos por cursos';
const listPracticasConstant = 'Listado de prácticas';

const buildStationText = (codEstacion: string | null, descEstacion: string | null): string => {
  if (!codEstacion && !descEstacion) {
    return '-';
  }

  const codEstacionText = codEstacion && codEstacion !== null ? codEstacion : '';
  const descEstacionText = descEstacion && descEstacion !== null ? descEstacion : '';

  return `${codEstacionText} - ${descEstacionText}`;
};

const buildNombrePerfil = (item: any): string => {
  if (item.usuario_nombre_perfil) {
    return item.usuario_nombre_perfil;
  }

  if (item.usuario_perfil_parsed === 'ALUMNO') {
    return `${item.usuario_perfil_parsed} - ${item.usuario_tipo_ie_parsed}`;
  }

  return item.usuario_perfil_parsed;
};


interface BuildPracticasParsedOptions {
  accessToAlumns: boolean;
  canSeeInstructor: boolean;
  canSeeAlumnInfo: boolean;
  canSeeUserInfo: boolean;
  canSeeClaimCheckField: boolean;
  practicasCount: number;
}

const buildCommonPracticeFields = (item: any, accessToAlumns: boolean): any[] => {
  const link = accessToAlumns ? '/listado-alumnos/' + item.ID_CURSO : '';

  return [
    {
      text: item.ID_CURSO,
      urlLink: link,
    },
    {
      text:
        parseDate(item.FECHA_INI_SERVICIO) +
        ' ' +
        parseTime(item.HORA_INI_SERVICIO),
    },
    {
      text:
        parseDate(item.FECHA_FIN_SERVICIO) +
        ' ' +
        parseTime(item.HORA_FIN_SERVICIO),
    },
    { text: item.MIN_PRACTICA },
    {
      text: item.ID_TIPO_PRACTICA !== '' ? item.DESC_PRACTICA : '',
    },
    {
      tooltip: [
        {
          title: 'NºTren: ',
        },
        {
          paragraph: item.NUM_TREN ? item.NUM_TREN : '-',
        },
        {
          title: 'Estación Inicio: ',
        },
        {
          paragraph: buildStationText(
            item.COD_ESTACION_INI,
            item.DESC_ESTACION_INI
          ),
        },
        {
          title: 'Estación Fin: ',
        },
        {
          paragraph: buildStationText(
            item.COD_ESTACION_FIN,
            item.DESC_ESTACION_FIN
          ),
        },
      ],
    },
  ];
};

const buildInfoUserFields = (item: any): any[] => [
  item.ID_ALUMNO
    ? {
        text: item.ID_ALUMNO + ' - ' + item.nombre_usuario_parsed,
        tooltip: [
          {
            title: 'Observaciones alumno: ',
          },
          {
            paragraph:
              item.OBSERVACIONES_ALUMNO !== null
                ? item.OBSERVACIONES_ALUMNO
                : 'No hay observaciones del alumno',
          },
        ],
        className: item.ID_ALUMNO ? 'position_icon' : '',
      }
    : {
        text: '',
      },
  {
    text: buildNombrePerfil(item),
  },
];

const buildInstructorFields = (item: any): any[] => [
  item.ID_INSTRUCTOR
    ? {
        text: item.ID_INSTRUCTOR
          ? item.ID_INSTRUCTOR + ' - ' + item.NOMBRE_INSTRUCTOR
          : '',
        tooltip: [
          {
            title: 'Observaciones instructor: ',
          },
          {
            paragraph:
              item.OBSERVACIONES_INSTRUCTOR !== null
                ? item.OBSERVACIONES_INSTRUCTOR
                : ' No hay observaciones del instructor',
          },
        ],
        className: item.ID_ALUMNO ? 'position_icon' : '',
      }
    : {
        text: '',
      },
];

const buildPracticeActionFields = (
  item: any,
  canSeeClaimCheckField: boolean,
  canSeeInstructor: boolean,
  practicasCount: number
): any[] => {
  const commonFields2: any = [
    {
      text: parsePracticeStatus(item.ID_EST_PRACTICA),
    },
    {
      urlIcon: RouteTypes.DETAIL.replace(':id', String(item?.ID_PRACTICA)),
      ariaLabelIcon: 'Abrir modal',
      label: item.ID_PRACTICA,
    },
  ];

  if ((canSeeClaimCheckField || !canSeeInstructor) && practicasCount !== 0) {
    commonFields2.splice(1, 0, {
      checkRow: 'false',
      label: item.ID_PRACTICA,
      data: item.ID_PRACTICA,
    });
  }

  return commonFields2;
};

const buildPracticeRow = (
  item: any,
  options: BuildPracticasParsedOptions
): any[] => {
  let data: any[] = buildCommonPracticeFields(item, options.accessToAlumns);

  if (options.canSeeInstructor || options.canSeeAlumnInfo) {
    data = [...data, ...buildInstructorFields(item)];
  }

  if (options.canSeeUserInfo) {
    data = [...data, ...buildInfoUserFields(item)];
  }

  return [
    ...data,
    ...buildPracticeActionFields(
      item,
      options.canSeeClaimCheckField,
      options.canSeeInstructor,
      options.practicasCount
    ),
  ];
};

const buildPracticasParsed = (
  practicas: any[],
  options: BuildPracticasParsedOptions
): never[] =>
  practicas.map((item) => ({
    table: [buildPracticeRow(item, options)],
  })) as never[];

const PracticasAlumno = (): ReactElement => {
  const dispatch = useDispatch();
  const history = useHistory();
  const [checkedPractice] = useState<any[]>([]);
  const [hideClaimButton, setHideClaimButton] = useState(false);
  const [hideValidButton, setHideValidButton] = useState(false);
  const [openInit] = useState(false);
  const currentLocation = useLocation();

  const params: CourseAlumn = useParams();

  const linkAnteriorCurso = RouteTypes.ALUMNS.replace(
    ':cursoId',
    params.cursoId
  ).replace('/:nombreCurso?', '');

  const [itemsOnLeftByProfile, setitemsOnLeftByProfile] = useState(5);

  const backToAlumnCurso = [
    {
      label: 'Listado de cursos',
      link: RouteTypes.COURSE,
      textHelp: 'Listado de cursos',
      active: false,
    },
    {
      label: listAlumnosCursosConstant,
      link: linkAnteriorCurso + '/back',
      textHelp: listAlumnosCursosConstant,
      active: false,
    },
    {
      label: 'Alumno',
      link: '#',
      textHelp: 'Alumno',
      active: true,
    },
  ];

  const backToListadoPracticas = [
    {
      label: listPracticasConstant,
      link: RouteTypes.HOME,
      textHelp: listPracticasConstant,
      active: false,
    },
    {
      label: listAlumnosCursosConstant,
      link: linkAnteriorCurso,
      textHelp: listAlumnosCursosConstant,
      active: false,
    },
    {
      label: 'Alumno',
      link: '#',
      textHelp: 'Alumno',
      active: true,
    },
  ];

  const defaultLinks = [
    {
      label: listPracticasConstant,
      link: '/home',
      textHelp: listPracticasConstant,
      active: true,
    },
  ];

  let links: typeof defaultLinks;

  const {
    access_token,
    msgSubmit,
    modalDraftType,
    profileActive,
    practicas,
    practicasCount,
    fechaMinimaPractica,
  } = useSelector<any, any>(selectors);
  const [list] = useState(true);
  const redirect = (e: any) => {
    history.push(e.detail);
  };

  const defaultEmptyFilter = {
    alumnos: params?.idAlumn || '',
    ascDesc: '',
    cursos: params?.cursoId || '',
    elemFin: 10,
    elemInicio: 0,
    estados: '',
    fechaFin: getTodayFinishDate(),
    fechaInicio: getTodayMinus15(),
    instructores: '',
    orden: '',
    sociedades: '',
    savedValues: false,
    pathName: '',
  };

  const [initFilter, setInitFilter] = useState(defaultEmptyFilter);

  /** PROFILE ACCESS */
  const canSeeAlumnExt = hasAccess(profileActive, [
    userProfile.alumno.extBecario,
    userProfile.alumno.extAspirante,
    userProfile.alumno.extMaquinista,
  ]);

  const canSeeInstructor = !hasAccess(profileActive, [userProfile.instructor]);

  const canSeeClaimCheckField = hasAccess(profileActive, [
    userProfile.gestor,
    userProfile.administrador,
    userProfile.formador,
  ]);

  const canSeeUserInfo = hasAccess(profileActive, [
    userProfile.gestor,
    userProfile.administrador,
    userProfile.instructor,
    userProfile.formador,
    userProfile.supervisor,
    userProfile.visualizador,
  ]);

  const accessToAlumns = hasAccess(profileActive, [
    userProfile.formador,
    userProfile.gestor,
    userProfile.administrador,
    userProfile.supervisor,
    userProfile.visualizador,
  ]);

  const canSeeAlumnInfo = hasAccess(profileActive, [
    userProfile.alumno.extBecario,
    userProfile.alumno.extAspirante,
    userProfile.alumno.extMaquinista,
    userProfile.alumno.intBecario,
    userProfile.alumno.intMaquinista,
    userProfile.formador,
    userProfile.gestor,
    userProfile.administrador,
    userProfile.supervisor,
    userProfile.visualizador,
  ]);
  /** end section */

  useEffect(() => {
    document.body.addEventListener('mousemove', () => {
      const aux = document.querySelector(
        'div.ui-lateral-menu > button.ui-lateral-menu__menu-button'
      );
      const isContained = aux?.classList.contains(
        'ui-lateral-menu__menu-button--open'
      );
      if (!isContained) {
        const uls = document.querySelectorAll(
          'div.ui-lateral-menu ul.ui-lateral-menu__sublink-list--open'
        );
        uls.forEach((ul) => {
          ul.classList.remove('ui-lateral-menu__sublink-list--open');
        });
      }
    });
  }, []);

  useEffect(() => {
    // Change filter when params change
    const filter = getDefaultStatus(profileActive, {
      ...initFilter,
      alumnos: params?.idAlumn,
      cursos: params?.cursoId,
      pathName: window.location.pathname,
    });
    setInitFilter(filter);
  }, [params, profileActive, window.location.pathname]);

  const currentProfileId = useRef(null);

  const {
    filterRef,
    data,
    setData,
    headers,
    setHeaders,
    handleFilterTable,
    handleFilterChange,
  } = useList(
    { ...initFilter },
    [],
    {
      action: (filter) => {
        return dispatch(getPracticas(filter));
      },
      headerToAPIMap: {
        'Inicio  Serv.': 'FECHA_INI_SERVICIO',
        'Fin  Serv.': 'FECHA_FIN_SERVICIO',
      },
    },
    (filter) => {
      dispatch(setPracticasCount2(0));
      dispatch(getPracticas(filter));
      dispatch(getPracticasCount(filter));
    },
    async () => {
      dispatch(setLoading(true));
      await new Promise((resolve) => setTimeout(resolve, 90));
      dispatch(setLoading(false));
    }
  );

  const handleExportExcel = () => {
    dispatch(
      getPracticasExcel({
        filter: { ...filterRef.current },
        excelConfig: {
          literals: headers,
          profile: profileActive,
        },
      })
    );
  };

  const handleSelectAll = (data) => {
    const newData = data.map((cell) => {
      return cell.data;
    });

    if (canSeeClaimCheckField) {
      dispatch(setPracticaReclamadaSeleccionada(newData.toString()));
    }
    if (!canSeeInstructor) {
      dispatch(setPracticaValidadaSeleccionada(newData.toString()));
    }
  };

  const handleSelectPractica = (e: any) => {
    const practiceSelected: any = checkedPractice;
    if (e.detail.checked === true) {
      practiceSelected.push(e.detail.data);
    } else {
      const pos = practiceSelected.indexOf(e.detail.data);
      practiceSelected.splice(pos, 1);
    }
    if (canSeeClaimCheckField) {
      dispatch(setPracticaReclamadaSeleccionada(practiceSelected.toString()));
    }
    if (!canSeeInstructor) {
      dispatch(setPracticaValidadaSeleccionada(practiceSelected.toString()));
    }
  };

  useEffect(() => {
    if (!profileActive) {
      return;
    }
    if (canSeeAlumnExt) {
      dispatch(getInfoUserProfile());
    }
    setHideClaimButton(canSeeClaimCheckField);
    setHideValidButton(!canSeeInstructor);
    const commonFields1 = [
      { text: LiteralHeadersTableTitles.CURSO },
      {
        text: 'Inicio  Serv.',
        orderBy: true,
        ariaLabelIcon: 'Icono de ordenar',
      },
      {
        text: 'Fin  Serv.',
        orderBy: true,
        ariaLabelIcon: 'Icono de ordenar',
      },
      { text: LiteralHeadersTableTitles.MINUTOS },
      { text: LiteralHeadersTableTitles.TIPO_PRACTICA },
      { text: LiteralHeadersTableTitles.TREN },
    ];

    const userInfoFields = [
      { text: LiteralHeadersTableTitles.ALUMNO },
      { text: LiteralHeadersTableTitles.NOMBRE_PERFIL },
    ];

    const instructorFields = [{ text: LiteralHeadersTableTitles.INSTRUCTOR }];

    const commonFields2: any = [
      { text: LiteralHeadersTableTitles.ESTADO, hideResponsive: false },
      {
        text: LiteralHeadersTableTitles.VER,
        hideResponsive: true,
      },
    ];

    if ((canSeeClaimCheckField || !canSeeInstructor) && practicasCount !== 0) {
      commonFields2.splice(1, 0, {
        text: '',
        checkRow: 'false',
        hideResponsive: false,
      });
    }

    let headers: any[] = [...commonFields1];

    if (canSeeInstructor) {
      headers = [...headers, ...instructorFields];
    }

    if (canSeeUserInfo) {
      headers = [...headers, ...userInfoFields];
    }

    headers = [...headers, ...commonFields2];

    setHeaders(headers);
  }, [practicas, profileActive, practicasCount]);

  useEffect(() => {
    if (!profileActive) {
      return;
    }

    setData(
      buildPracticasParsed(practicas, {
        accessToAlumns,
        canSeeInstructor,
        canSeeAlumnInfo,
        canSeeUserInfo,
        canSeeClaimCheckField,
        practicasCount,
      })
    );
  }, [practicas, profileActive, practicasCount]);

  useEffect(() => {
    const getFechaDesdeListAlumn = (): number => {
      if (filterRef.current.savedValues === false) {
        if (
          filterRef.current.alumnos !== '' &&
          filterRef.current.cursos !== ''
        ) {
          return fechaMinimaPractica?.FECHA_TIMESTAMP?.toString();
        } else {
          return getTodayMinus15();
        }
      } else {
        return filterRef.current.fechaInicio;
      }
    };
    const profileChange =
      currentProfileId.current &&
      currentProfileId.current !== profileActive.ID_PERFIL;
    const init =
      profileActive &&
      fechaMinimaPractica.FECHA_TIMESTAMP &&
      !filterRef.current.fechaInicio;
    if (init || profileChange) {
      currentProfileId.current = profileActive.ID_PERFIL;
      filterRef.current = getDefaultStatus(profileActive, filterRef.current);
      filterRef.current.sociedades = '';
      filterRef.current.fechaInicio = getFechaDesdeListAlumn();
      dispatch(getPracticas(filterRef.current));
      dispatch(getPracticasCount(filterRef.current));
    }
  }, [profileActive, fechaMinimaPractica]);

  useEffect(() => {
    if (profileActive) {
      switch (profileActive.ID_PERFIL) {
        case '111':
        case '121':
        case '122':
        case '123':
          setitemsOnLeftByProfile(4);
          break;
        case '211':
        case '311':
        case '411':
        case '511':
        case '611':
        case '711':
          setitemsOnLeftByProfile(5);
          break;
      }
    }
  }, [profileActive]);

  if (!access_token) {
    return <Redirect to={RouteTypes.LOGIN} push />;
  }

  if (!currentLocation.pathname.includes(RouteTypes.HOME)) {
    links = backToAlumnCurso;
  } else if (params.cursoId && params.idAlumn) {
    links = backToListadoPracticas;
  } else {
    links = defaultLinks;
  }

  return (
    <>
      <Header />
      <div className="rf-layout-table rf-practice-list">
        <RfUiBreadcrumbs
          links={JSON.stringify(links)}
          onEventChange={(e: any) => {
            history.push(e.detail);
          }}
        ></RfUiBreadcrumbs>
        <h1 className="rf-title rf-title--simple">
          {initFilter.alumnos &&
          !initFilter.savedValues &&
          window.location.pathname !== RouteTypes.HOME
            ? 'Listado de prácticas - ' +
              initFilter.cursos +
              ' - ' +
              initFilter.alumnos
            : listPracticasConstant}
        </h1>
        <AlumnPracticeListFilter
          handleSubmit={handleFilterChange}
          initValues={initFilter}
        />
        <Table
          openInit={openInit}
          redirect={(e: any) => {
            redirect(e);
          }}
          list={list}
          headers={headers}
          data={data}
          orderMobile={true}
          orderOptions={options}
          dataCount={practicasCount}
          handleFilter={handleFilterTable}
          hideAudit={true}
          itemsOnLeft={itemsOnLeftByProfile}
          hideClaim={!hideClaimButton}
          hideValid={!hideValidButton}
          showButtonDetail={true}
          handleExportExcel={handleExportExcel}
          handleCheckAll={handleSelectAll}
          handleCheckPractica={handleSelectPractica}
        />
        <RfUiModal
          title-modal={msgSubmit}
          modalConfirmText="Aceptar"
          id-modal="modalDraft"
          modal-type={modalDraftType}
          onModalClose={(e: any) => {
            dispatch(setMsgSubmit(''));
            dispatch(setModalDraftType(''));
          }}
          onConfirm={(e: any) => {
            dispatch(setMsgSubmit(''));
            dispatch(setModalDraftType(''));
          }}
        ></RfUiModal>
      </div>
    </>
  );
};

export default PracticasAlumno;
